import BaseComponent from '../base-component/base-component'
import {
  autoUpdate,
  computePosition,
  offset,
  flip,
  shift,
  arrow,
} from '@floating-ui/dom'
import { createPortal } from '../../utils/utils'
import { bodyElement } from '../../components/common/body-element'
import { getTemplateFirstChildContent } from '../../utils/dom'
import FloatElementBtn from './float-element-btn'
import { Nullable } from '../../types/common'

class FloatElement extends BaseComponent {
  initialPlacement: string
  withOverlay: boolean
  withPortal: boolean
  element: HTMLElement
  arrow: Nullable<HTMLElement>

  constructor() {
    super()

    this.withOverlay = this.hasAttribute('data-with-overlay')

    const template = this.querySelector(
      `[data-float-element-template="${this.id}"]`,
    )

    this.element = getTemplateFirstChildContent(template)
    this.arrow = this.element.querySelector('[data-float-element-arrow]')
    this.withPortal = this.hasAttribute('with-portal')

    this.initialPlacement = this.dataset.placement

    if (this.withPortal) {
      createPortal(this)
    }
  }

  connectedCallback(): void {
    this.init()

    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this.handleBlockDeselect)
    }
  }

  disconnectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.destroy()
    }

    window.removeEventListener('wheel', this.handleScroll)
  }

  handleScroll = (): void => {
    if (this.type === 'tooltip') {
      this.hide()

      window.removeEventListener('wheel', this.handleScroll)
    }
  }

  handleBlockSelect = (event): void => {
    if (this.dataset.sectionId === event.detail.sectionId) {
      const target = this.element.querySelector(
        `[block-id="${event.detail.blockId}"]`,
      )
      const blockId = this.getAttribute('block-id')
      const isTargetBlock = blockId === event.detail.blockId || target

      if (!isTargetBlock) {
        return
      }

      if (this.btn) {
        this.btn.trigger()
      } else {
        this.hide()
      }
    }
  }

  handleBlockDeselect = (event): void => {
    if (this.dataset.sectionId === event.detail.sectionId) {
      const target = this.element.querySelector(
        `[block-id="${event.detail.blockId}"]`,
      )
      const blockId = this.getAttribute('block-id')
      const isTargetBlock = blockId === event.detail.blockId || target

      if (!isTargetBlock) {
        return
      }

      this.hide()
    }
  }

  init = (): void => {
    const floatingElement =
      document.getElementById(this.dataset.floatingElementId) || this.btn

    if (!floatingElement) {
      return
    }

    autoUpdate(floatingElement, this, () => {
      computePosition(floatingElement, this, this.getOptions() as any).then(
        data => {
          if (this.dataset.placement !== data.placement) {
            this.dataset.placement = data.placement
          }

          this.updatePosition(data)

          if (this.arrow) {
            this.updateArrow(data)
          }
        },
      )
    })
  }

  updatePosition({ x, y }): void {
    Object.assign(this.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  getOptions() {
    return {
      placement: this.dataset.placement,
      middleware: this.getMiddleware(),
      strategy: this.strategy,
    }
  }

  getMiddleware() {
    const alignmentAxis = this.dataset.alignmentAxisOffset || 0
    const mainAxis = this.dataset.mainAxisOffset || 0

    const middleware = [
      offset({
        alignmentAxis: +alignmentAxis,
        mainAxis: +mainAxis,
      }),
      flip(),
      shift(),
    ]

    if (this.arrow) middleware.push(arrow({ element: this.arrow }))

    return middleware
  }

  checkCursorOutside(x, y) {
    const mainAxisOffset = this.dataset.mainAxisOffset || 0
    const offset = +mainAxisOffset
    const interactiveBorder = 2

    const boundaries = this.getBoundingClientRect()

    const exceedsTop = boundaries.top - (y + offset) > interactiveBorder
    const exceedsBottom = y - boundaries.bottom - offset > interactiveBorder
    const exceedsLeft = boundaries.left - (x + offset) > interactiveBorder
    const exceedsRight = x - boundaries.right - offset > interactiveBorder

    return exceedsTop || exceedsBottom || exceedsLeft || exceedsRight
  }

  updateArrow({ placement, middlewareData }) {
    if (!this.arrow) {
      return
    }

    const { x: arrowX, y: arrowY } = middlewareData.arrow

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]]

    Object.assign(this.arrow.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-5px',
    })
  }

  mount(): void {
    if (!this.contains(this.element)) {
      this.appendChild(this.element)
    }
  }

  unmount(): void {
    if (this.isOpen && this.withOverlay) {
      bodyElement.hideOverlay(this.id)
    }

    if (this.element) {
      this.element.remove()
    }
  }

  show = () => {
    if (this.type === 'tooltip' && bodyElement.isScrolling) return

    const videos = this.element.querySelectorAll('video')

    videos.forEach(video => {
      video.play()
    })

    this.mount()

    this.setVisible(true)

    this.emit('show', {})

    window.addEventListener('wheel', this.handleScroll)
  }

  hide = () => {
    this.setVisible(false)

    this.unmount()

    this.emit('hide', {})

    this.dataset.placement = this.initialPlacement
  }

  setVisible(isVisible) {
    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

    if (this.btn) {
      this.btn.setExpand(isVisible)
    }

    if (this.withOverlay) {
      const withBlur = this.hasAttribute('data-overlay-with-blur')

      const styles = {
        backdropFilter: withBlur ? `blur(16.5px)` : 'none',
      }

      this.isOpen
        ? bodyElement.showOverlay(this.id, styles)
        : bodyElement.hideOverlay(this.id)
    }
  }

  get btn(): Nullable<FloatElementBtn> {
    return document.querySelector(
      `[data-float-element-id="${this.id}"], [data-tooltip-id="${this.id}"]`,
    )
  }

  get isOpen() {
    return this.getAttribute('aria-hidden') === 'false'
  }

  get strategy() {
    return this.dataset.strategy || 'absolute'
  }

  get type() {
    return this.dataset.type
  }
}

export default FloatElement
